/**
 * ExecutionHistory Component
 *
 * Displays execution history for processes with filtering,
 * sorting, and detailed execution information.
 * Can be used standalone or embedded in ProcessBuilderDetail.
 *
 * @component
 */

import React, { useState, useMemo } from 'react';
import {
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  Search,
  Filter,
  ChevronDown,
  ChevronRight,
  Download,
  ExternalLink,
  Activity,
  Zap,
  Database,
  Mail,
  FileText,
  Users,
  Send,
  Workflow
} from 'lucide-react';
import {
  StandardButton,
  StandardCard,
  StandardModal,
  StandardInput,
  StandardSelect
} from '../../standard';
import type {
  ProcessExecution,
  ExecutedAction,
  ActionType
} from '../../../types/processBuilder';

// ============================================
// INLINE BADGE COMPONENT
// ============================================

interface BadgeProps {
  children: React.ReactNode;
  variant?: 'success' | 'warning' | 'danger' | 'info' | 'secondary';
  size?: 'sm' | 'md';
}

const Badge: React.FC<BadgeProps> = ({ children, variant = 'secondary', size = 'md' }) => {
  const variantClasses = {
    success: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
    warning: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
    danger: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
    info: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
    secondary: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
  };

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm'
  };

  return (
    <span className={`inline-flex items-center font-medium rounded-full ${variantClasses[variant]} ${sizeClasses[size]}`}>
      {children}
    </span>
  );
};

// ============================================
// INTERFACES
// ============================================

interface ExecutionHistoryProps {
  executions: ProcessExecution[];
  processName?: string;
  onRefresh?: () => void;
  onViewRecord?: (recordId: string, objectType: string) => void;
  showFilters?: boolean;
  maxItems?: number;
  compact?: boolean;
}

interface ExecutionFilters {
  status: string;
  dateRange: string;
  searchQuery: string;
}

// ============================================
// CONSTANTS
// ============================================

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'running', label: 'Running' },
  { value: 'partial', label: 'Partial' }
];

const DATE_RANGE_OPTIONS = [
  { value: '', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'Last 7 Days' },
  { value: 'month', label: 'Last 30 Days' },
  { value: 'quarter', label: 'Last 90 Days' }
];

const ACTION_ICONS: Record<ActionType, React.ReactNode> = {
  field_update: <Database size={12} />,
  create_record: <FileText size={12} />,
  update_related: <Database size={12} />,
  send_email: <Mail size={12} />,
  post_to_feed: <Send size={12} />,
  create_task: <FileText size={12} />,
  submit_for_approval: <Users size={12} />,
  call_apex: <Zap size={12} />,
  invoke_process: <Workflow size={12} />
};

// ============================================
// HELPER FUNCTIONS
// ============================================

const formatDuration = (ms?: number): string => {
  if (!ms) return '-';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(2)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
};

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleString();
};

const formatRelativeTime = (dateStr: string): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const isWithinDateRange = (dateStr: string, range: string): boolean => {
  if (!range) return true;

  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = diffMs / 86400000;

  switch (range) {
    case 'today':
      return date.toDateString() === now.toDateString();
    case 'week':
      return diffDays <= 7;
    case 'month':
      return diffDays <= 30;
    case 'quarter':
      return diffDays <= 90;
    default:
      return true;
  }
};

// ============================================
// STATUS ICON COMPONENT
// ============================================

interface StatusIconProps {
  status: string;
  size?: number;
}

const StatusIcon: React.FC<StatusIconProps> = ({ status, size = 16 }) => {
  switch (status) {
    case 'completed':
      return <CheckCircle size={size} className="text-green-500" />;
    case 'failed':
      return <XCircle size={size} className="text-red-500" />;
    case 'running':
      return <RefreshCw size={size} className="text-blue-500 animate-spin" />;
    case 'partial':
      return <AlertTriangle size={size} className="text-yellow-500" />;
    default:
      return <Clock size={size} className="text-gray-400" />;
  }
};

// ============================================
// EXECUTION ROW COMPONENT
// ============================================

interface ExecutionRowProps {
  execution: ProcessExecution;
  compact?: boolean;
  onSelect: () => void;
  onViewRecord?: (recordId: string, objectType: string) => void;
}

const ExecutionRow: React.FC<ExecutionRowProps> = ({
  execution,
  compact = false,
  onSelect,
  onViewRecord
}) => {
  const successCount = execution.executedActions.filter(a => a.status === 'success').length;
  const failedCount = execution.executedActions.filter(a => a.status === 'failed').length;

  if (compact) {
    return (
      <div
        onClick={onSelect}
        className="flex items-center gap-3 p-2 hover:bg-gray-50 dark:hover:bg-gray-800 rounded cursor-pointer transition-colors"
      >
        <StatusIcon status={execution.status} size={14} />
        <span className="flex-1 text-sm text-gray-900 dark:text-gray-100 truncate">
          {execution.recordName || execution.recordId}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {formatRelativeTime(execution.startTime)}
        </span>
      </div>
    );
  }

  return (
    <div
      onClick={onSelect}
      className="flex items-center gap-4 p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md cursor-pointer transition-all"
    >
      <StatusIcon status={execution.status} size={20} />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
            {execution.recordName || execution.recordId}
          </span>
          <Badge variant="secondary" size="sm">
            {execution.objectType}
          </Badge>
          {execution.matchedCriteriaGroupName && (
            <Badge variant="info" size="sm">
              {execution.matchedCriteriaGroupName}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-gray-400">
          <span>{formatDate(execution.startTime)}</span>
          <span>•</span>
          <span>v{execution.versionNumber}</span>
          {execution.triggerType && (
            <>
              <span>•</span>
              <span>{execution.triggerType.replace(/_/g, ' ')}</span>
            </>
          )}
        </div>
      </div>

      <div className="flex items-center gap-4">
        {/* Action Summary */}
        <div className="flex items-center gap-2">
          {successCount > 0 && (
            <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
              <CheckCircle size={12} />
              {successCount}
            </span>
          )}
          {failedCount > 0 && (
            <span className="flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
              <XCircle size={12} />
              {failedCount}
            </span>
          )}
        </div>

        {/* Duration */}
        <div className="text-right w-20">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {formatDuration(execution.duration)}
          </p>
        </div>

        {/* View Record Link */}
        {onViewRecord && (
          <StandardButton
            size="xs"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              onViewRecord(execution.recordId, execution.objectType);
            }}
          >
            <ExternalLink size={14} />
          </StandardButton>
        )}

        <ChevronRight size={16} className="text-gray-400" />
      </div>
    </div>
  );
};

// ============================================
// EXECUTION DETAIL MODAL
// ============================================

interface ExecutionDetailModalProps {
  execution: ProcessExecution;
  onClose: () => void;
  onViewRecord?: (recordId: string, objectType: string) => void;
}

const ExecutionDetailModal: React.FC<ExecutionDetailModalProps> = ({
  execution,
  onClose,
  onViewRecord
}) => {
  const [expandedActions, setExpandedActions] = useState<Set<string>>(new Set());

  const toggleAction = (actionId: string) => {
    setExpandedActions(prev => {
      const next = new Set(prev);
      if (next.has(actionId)) {
        next.delete(actionId);
      } else {
        next.add(actionId);
      }
      return next;
    });
  };

  return (
    <StandardModal
      isOpen={true}
      onClose={onClose}
      title="Execution Details"
      size="lg"
    >
      <div className="space-y-6">
        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Status
            </p>
            <div className="flex items-center gap-2 mt-1">
              <StatusIcon status={execution.status} size={18} />
              <span className="font-medium capitalize text-gray-900 dark:text-gray-100">
                {execution.status}
              </span>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Duration
            </p>
            <p className="font-medium text-gray-900 dark:text-gray-100 mt-1">
              {formatDuration(execution.duration)}
            </p>
          </div>

          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Actions
            </p>
            <p className="font-medium text-gray-900 dark:text-gray-100 mt-1">
              {execution.executedActions.length} executed
            </p>
          </div>

          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Version
            </p>
            <p className="font-medium text-gray-900 dark:text-gray-100 mt-1">
              v{execution.versionNumber}
            </p>
          </div>
        </div>

        {/* Record Info */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Triggered By Record
              </p>
              <p className="font-medium text-gray-900 dark:text-gray-100 mt-1">
                {execution.recordName || execution.recordId}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {execution.objectType} • {execution.triggerType?.replace(/_/g, ' ')}
              </p>
            </div>
            {onViewRecord && (
              <StandardButton
                size="sm"
                variant="secondary"
                onClick={() => onViewRecord(execution.recordId, execution.objectType)}
                leftIcon={<ExternalLink size={14} />}
              >
                View Record
              </StandardButton>
            )}
          </div>
        </div>

        {/* Decision Group Match */}
        {execution.matchedCriteriaGroupName && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <CheckCircle size={16} className="text-blue-600 dark:text-blue-400" />
              <span className="text-sm text-blue-800 dark:text-blue-200">
                Matched decision group: <strong>{execution.matchedCriteriaGroupName}</strong>
              </span>
            </div>
          </div>
        )}

        {/* Error Message */}
        {execution.errorMessage && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <XCircle size={16} className="text-red-600 dark:text-red-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800 dark:text-red-200">
                  Execution Error
                </p>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                  {execution.errorMessage}
                </p>
                {execution.errorDetails && (
                  <pre className="mt-2 text-xs text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30 p-2 rounded overflow-auto max-h-32">
                    {execution.errorDetails}
                  </pre>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Executed Actions */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
            Executed Actions ({execution.executedActions.length})
          </h4>

          {execution.executedActions.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 italic">
              No actions were executed
            </p>
          ) : (
            <div className="space-y-2">
              {execution.executedActions.map((action, index) => (
                <div
                  key={action.actionId || index}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                >
                  <button
                    onClick={() => toggleAction(action.actionId)}
                    className="w-full flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <StatusIcon status={action.status} size={16} />
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 dark:text-gray-400">
                        {ACTION_ICONS[action.actionType]}
                      </span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {action.actionName}
                      </span>
                    </div>
                    <span className="flex-1" />
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {formatDuration(action.duration)}
                    </span>
                    {expandedActions.has(action.actionId) ? (
                      <ChevronDown size={16} className="text-gray-400" />
                    ) : (
                      <ChevronRight size={16} className="text-gray-400" />
                    )}
                  </button>

                  {expandedActions.has(action.actionId) && (
                    <div className="p-3 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Action Type</p>
                          <p className="text-gray-900 dark:text-gray-100">
                            {action.actionType.replace(/_/g, ' ')}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Executed At</p>
                          <p className="text-gray-900 dark:text-gray-100">
                            {formatDate(action.executedAt)}
                          </p>
                        </div>
                      </div>

                      {action.resultDetails && (
                        <div className="mt-3">
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Result Details</p>
                          <pre className="text-xs bg-gray-100 dark:bg-gray-800 p-2 rounded overflow-auto max-h-24">
                            {action.resultDetails}
                          </pre>
                        </div>
                      )}

                      {action.errorMessage && (
                        <div className="mt-3 p-2 bg-red-50 dark:bg-red-900/20 rounded">
                          <p className="text-xs text-red-800 dark:text-red-200">
                            Error: {action.errorMessage}
                          </p>
                        </div>
                      )}

                      {action.createdRecordId && (
                        <div className="mt-3">
                          <p className="text-xs text-gray-500 dark:text-gray-400">Created Record</p>
                          <p className="text-sm text-gray-900 dark:text-gray-100 font-mono">
                            {action.createdRecordId}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Timestamps */}
        <div className="text-xs text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700 pt-4">
          <div className="flex justify-between">
            <span>Started: {formatDate(execution.startTime)}</span>
            {execution.endTime && <span>Ended: {formatDate(execution.endTime)}</span>}
          </div>
        </div>
      </div>
    </StandardModal>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

export const ExecutionHistory: React.FC<ExecutionHistoryProps> = ({
  executions,
  processName,
  onRefresh,
  onViewRecord,
  showFilters = true,
  maxItems = 50,
  compact = false
}) => {
  const [filters, setFilters] = useState<ExecutionFilters>({
    status: '',
    dateRange: '',
    searchQuery: ''
  });
  const [selectedExecution, setSelectedExecution] = useState<ProcessExecution | null>(null);

  // Filter executions
  const filteredExecutions = useMemo(() => {
    return executions.filter(exec => {
      // Status filter
      if (filters.status && exec.status !== filters.status) return false;

      // Date range filter
      if (filters.dateRange && !isWithinDateRange(exec.startTime, filters.dateRange)) return false;

      // Search filter
      if (filters.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        const matchesRecord = (exec.recordName?.toLowerCase() || exec.recordId.toLowerCase()).includes(query);
        const matchesCriteria = exec.matchedCriteriaGroupName?.toLowerCase().includes(query);
        if (!matchesRecord && !matchesCriteria) return false;
      }

      return true;
    }).slice(0, maxItems);
  }, [executions, filters, maxItems]);

  // Calculate summary stats
  const stats = useMemo(() => {
    const total = filteredExecutions.length;
    const completed = filteredExecutions.filter(e => e.status === 'completed').length;
    const failed = filteredExecutions.filter(e => e.status === 'failed').length;
    const avgDuration = total > 0
      ? Math.round(filteredExecutions.reduce((sum, e) => sum + (e.duration || 0), 0) / total)
      : 0;

    return { total, completed, failed, avgDuration };
  }, [filteredExecutions]);

  // Export to CSV
  const handleExport = () => {
    const headers = ['Record', 'Status', 'Started', 'Duration', 'Actions', 'Matched Group'];
    const rows = filteredExecutions.map(exec => [
      exec.recordName || exec.recordId,
      exec.status,
      exec.startTime,
      formatDuration(exec.duration),
      exec.executedActions.length,
      exec.matchedCriteriaGroupName || ''
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `execution-history-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (executions.length === 0) {
    return (
      <div className="text-center py-12">
        <Activity size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
          No Executions Yet
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {processName
            ? `The process "${processName}" hasn't been executed yet.`
            : 'No process executions have been recorded yet.'}
        </p>
        {onRefresh && (
          <StandardButton
            variant="secondary"
            size="sm"
            onClick={onRefresh}
            leftIcon={<RefreshCw size={14} />}
            className="mt-4"
          >
            Refresh
          </StandardButton>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters and Controls */}
      {showFilters && !compact && (
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px]">
            <StandardInput
              placeholder="Search by record..."
              value={filters.searchQuery}
              onChange={(e) => setFilters(prev => ({ ...prev, searchQuery: e.target.value }))}
              leftIcon={<Search size={16} />}
            />
          </div>

          <StandardSelect
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            options={STATUS_OPTIONS}
          />

          <StandardSelect
            value={filters.dateRange}
            onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e.target.value }))}
            options={DATE_RANGE_OPTIONS}
          />

          <div className="flex items-center gap-2">
            {onRefresh && (
              <StandardButton
                size="sm"
                variant="secondary"
                onClick={onRefresh}
                leftIcon={<RefreshCw size={14} />}
              >
                Refresh
              </StandardButton>
            )}
            <StandardButton
              size="sm"
              variant="ghost"
              onClick={handleExport}
              leftIcon={<Download size={14} />}
            >
              Export
            </StandardButton>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      {!compact && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {stats.total}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Total</p>
          </div>
          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-green-600 dark:text-green-400">
              {stats.completed}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Completed</p>
          </div>
          <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-red-600 dark:text-red-400">
              {stats.failed}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Failed</p>
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {formatDuration(stats.avgDuration)}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Avg Duration</p>
          </div>
        </div>
      )}

      {/* Executions List */}
      <div className={compact ? 'space-y-1' : 'space-y-2'}>
        {filteredExecutions.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <Filter size={24} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">No executions match your filters</p>
          </div>
        ) : (
          filteredExecutions.map((execution) => (
            <ExecutionRow
              key={execution.id}
              execution={execution}
              compact={compact}
              onSelect={() => setSelectedExecution(execution)}
              onViewRecord={onViewRecord}
            />
          ))
        )}
      </div>

      {/* Show More Indicator */}
      {executions.length > maxItems && (
        <p className="text-center text-sm text-gray-500 dark:text-gray-400">
          Showing {filteredExecutions.length} of {executions.length} executions
        </p>
      )}

      {/* Execution Detail Modal */}
      {selectedExecution && (
        <ExecutionDetailModal
          execution={selectedExecution}
          onClose={() => setSelectedExecution(null)}
          onViewRecord={onViewRecord}
        />
      )}
    </div>
  );
};

export default ExecutionHistory;
